import React, { useState, useRef, useEffect, useCallback } from 'react';

const DEFAULT_POSITION = { x: 0, y: 0 };

export const DraggableElement = ({ 
  id, 
  children, 
  editMode, 
  onPositionChange,
  initialStyle = {},
  snapToGrid = false,
  gridSize = 10,
  showMarkers = true,
  selectedElements = [],
  elementPositions = {},
  onSelectElement,
  dragAxis = 'both',
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [lastPosition, setLastPosition] = useState({ x: 0, y: 0 });
  const elementRef = useRef(null);

  const isSelected = selectedElements.includes(id);
  
  // Get position from central state (App.js)
  const position = elementPositions[id] || DEFAULT_POSITION;

  const snapPosition = useCallback((pos) => {
    if (!snapToGrid) return pos;

    const snappedX = dragAxis === 'y' ? pos.x : Math.round(pos.x / gridSize) * gridSize;
    const snappedY = dragAxis === 'x' ? pos.y : Math.round(pos.y / gridSize) * gridSize;

    return {
      x: snappedX,
      y: snappedY,
    };
  }, [dragAxis, gridSize, snapToGrid]);

  const handleMouseDown = (e) => {
    if (!editMode) return;

    // Selection handling
    if (onSelectElement) {
      onSelectElement(id, e);
    }

    // Start dragging
    setIsDragging(true);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
    });
    setLastPosition(position);
    e.preventDefault();
    e.stopPropagation();
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !editMode) return;

    const rawDeltaX = e.clientX - dragStart.x;
    const rawDeltaY = e.clientY - dragStart.y;
    const deltaX = dragAxis === 'y' ? 0 : rawDeltaX;
    const deltaY = dragAxis === 'x' ? 0 : rawDeltaY;

    const newX = lastPosition.x + deltaX;
    const newY = lastPosition.y + deltaY;

    // Calculate delta from last frame
    const frameDeltaX = deltaX - (position.x - lastPosition.x);
    const frameDeltaY = deltaY - (position.y - lastPosition.y);

    if (onPositionChange) {
      onPositionChange(id, { 
        x: newX, 
        y: newY, 
        deltaX: frameDeltaX, 
        deltaY: frameDeltaY, 
        isDragging: true 
      });
    }
  }, [dragAxis, dragStart.x, dragStart.y, editMode, id, isDragging, lastPosition.x, lastPosition.y, onPositionChange, position.x, position.y]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      
      // Apply snap on mouse up
      const snappedPos = snapPosition(position);
      
      if (onPositionChange) {
        onPositionChange(id, { ...snappedPos, isDragging: false });
      }
    }
  }, [id, isDragging, onPositionChange, position, snapPosition]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [handleMouseMove, handleMouseUp, isDragging]);

  const baseTransformStyles = {
    position: 'relative',
    transform: dragAxis === 'y'
      ? `translate(0px, ${position.y}px)`
      : (dragAxis === 'x' ? `translate(${position.x}px, 0px)` : `translate(${position.x}px, ${position.y}px)`),
    transition: isDragging ? 'none' : 'transform 0.2s ease',
  };

  const editModeStyles = editMode ? {
    outline: showMarkers
      ? (isSelected ? '3px solid #3B82F6' : '2px dashed #E07000')
      : 'none',
    outlineOffset: '4px',
    cursor: dragAxis === 'y' ? 'ns-resize' : (dragAxis === 'x' ? 'ew-resize' : 'move'),
    zIndex: isDragging ? 9999 : (isSelected ? 100 : 'auto'),
    transition: isDragging ? 'none' : 'transform 0.2s ease, outline 0.15s ease',
    userSelect: 'none',
    background: isSelected ? 'rgba(59, 130, 246, 0.05)' : 'transparent',
  } : {};

  const markerPositionLabel = dragAxis === 'y'
    ? (position.y !== 0 ? `(y: ${position.y})` : '')
    : (dragAxis === 'x'
      ? (position.x !== 0 ? `(x: ${position.x})` : '')
      : (position.x !== 0 || position.y !== 0 ? `(${position.x}, ${position.y})` : ''));

  return (
    <div
      ref={elementRef}
      onMouseDown={handleMouseDown}
      style={{
        ...initialStyle,
        ...baseTransformStyles,
        ...editModeStyles,
      }}
      data-element-id={id}
      data-draggable={editMode ? 'true' : 'false'}
      data-selected={isSelected ? 'true' : 'false'}
    >
      {editMode && showMarkers && (
        <div
          data-print-hidden="true"
          style={{
            position: 'absolute',
            top: '-24px',
            left: '0',
            background: isSelected ? '#3B82F6' : '#E07000',
            color: 'white',
            padding: '3px 10px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 'bold',
            fontFamily: 'Courier New, monospace',
            zIndex: 10000,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}
        >
          {isSelected && '✓ '}
          {id} {markerPositionLabel}
        </div>
      )}
      {children}
    </div>
  );
};
